import type { BehaviorSettingsInput, CustomBehaviorInput } from '../../behaviors/index.js'
import type { FacilityAddress, FacilityConfig } from '../../facilities/index.js'
import type { EmploymentType, WorkerRole, WorkerTier } from '../../core/index.js'
import type { CalloutEvent, CustomBehaviorEvent, NoShowEvent, ShiftCompleteEvent } from '../../partner/index.js'
import { DEFAULT_FACILITY_CONFIG, DEFAULT_SCHEDULING_RULES } from '../../facilities/index.js'
import { demoMinimumStaffingFor, demoStaffingPlan } from './demoStaffing.js'
import { TRAINING_FACILITY, TRAINING_OPERATOR, TRAINING_WORKER_IDS, buildTrainingOutcomeEvents, trainingWorkerRows } from './trainingCast.js'
import type { DemoActiveWorker, DemoPlan } from './demoPlan.js'
import { RANKING_POOL_SIZE } from './demoRules.js'

/**
 * The fake data a demo client is set up with. Everything here is invented:
 * the facilities, and every worker (generated once with the API's
 * seed-roster tool, then frozen here). It is the same for every demo client
 * on purpose - facility and worker ids only have to be unique within a
 * client, so one fixed, reviewed dataset is simpler and more predictable to
 * demo from than a fresh random one each time. Only the logins differ per
 * client, because login emails are unique across the whole platform.
 *
 * How much of it a given demo gets is a `DemoPlan` (demoPlan.ts): every
 * builder below takes the plan and reads only the facilities and workers
 * it names, so a minimum demo is a subset of the same reviewed data. A
 * large demo adds generated facilities and rosters around it (demoLargeData.ts).
 */

export interface DemoFacility {
  facilityId: string
  name: string
  /** IANA zone the facility's days are reckoned in. */
  timezone: string
  /** Invented. Recorded by the demo's operator login, since only an operator may set a facility's address. */
  address: FacilityAddress
  /** Invented, recorded with the address. */
  phone?: string
  /** The fake person set up as this facility's administrator, with an invented direct line (the facility's main number where none is given). */
  administrator: { firstName: string; lastName: string; phone?: string }
}

export const DEMO_FACILITIES: readonly DemoFacility[] = [
  {
    facilityId: 'north-campus',
    name: 'North Campus',
    timezone: 'America/New_York',
    address: { line1: '1200 Northgate Avenue', city: 'Albany', region: 'NY', postalCode: '12207', country: 'US' },
    phone: '(518) 555-0120',
    administrator: { firstName: 'Nadia', lastName: 'Whitlock', phone: '(518) 555-0121' },
  },
  {
    facilityId: 'riverside',
    name: 'Riverside',
    timezone: 'America/New_York',
    address: { line1: '75 Riverside Parkway', line2: 'Building C', city: 'Hartford', region: 'CT', postalCode: '06103', country: 'US' },
    phone: '(860) 555-0175',
    administrator: { firstName: 'Rafael', lastName: 'Okafor', phone: '(860) 555-0176' },
  },
  {
    facilityId: 'lakeview',
    name: 'Lakeview',
    timezone: 'America/Chicago',
    address: { line1: '3300 Lakeview Road', city: 'Madison', region: 'WI', postalCode: '53704', country: 'US' },
    phone: '(608) 555-0133',
    administrator: { firstName: 'Lena', lastName: 'Marchetti', phone: '(608) 555-0134' },
  },
  // The training materials' facility, with the cast as its roster and their facility manager as its administrator (trainingCast.ts).
  TRAINING_FACILITY,
]

/** The facility set up with partner prioritization switched off. */
export const DEMO_UNPRIORITIZED_FACILITY_ID = 'riverside'

/** What a demo client's tokens need for everything the demo sets up: partner events, facility self-service and rankings. */
export const DEMO_REQUIRED_SCOPES = ['events:write', 'facilities:manage', 'rankings:read', 'performance:read', 'census:write'] as const

/** The second operator, alongside the contact entered on the form: the training materials' operator. */
export const DEMO_SECOND_OPERATOR = TRAINING_OPERATOR

/**
 * [facilityId, name, role, score, chartingPct] - a few names repeat within a
 * facility on purpose: identity is the workerId, never the name. There is no
 * call-out reading here: every fixed-roster worker has reported outcomes,
 * and the roster's call-outs are counted from those (`currentMonthCallOuts`).
 */
const DEMO_WORKER_ROWS: readonly (readonly [string, string, WorkerRole, number, number])[] = [
  ["north-campus", "Amelie O'Reilly", "CNA", 80, 91],
  ["north-campus", "Sherri Simonis", "RN", 60, 84],
  ["north-campus", "Marty Bruen", "RN", 53, 80],
  ["north-campus", "Orlo Oberbrunner", "CNA", 57, 81],
  ["north-campus", "Chaim McKenzie", "RN", 83, 91],
  ["north-campus", "Kamron Bechtelar", "CNA", 37, 73],
  ["north-campus", "Chelsie Torp", "LPN", 78, 92],
  ["north-campus", "Manuel Zieme", "RN", 49, 78],
  ["north-campus", "Virgil Aufderhar", "LPN", 96, 100],
  ["north-campus", "Pietro Stark", "RN", 92, 98],
  ["north-campus", "Mertie Jones", "RN", 51, 79],
  ["north-campus", "Brant Herman", "RN", 70, 85],
  ["north-campus", "Nyah Miller", "RN", 85, 91],
  ["north-campus", "Dax Ferry", "RN", 97, 96],
  ["north-campus", "Chelsie Torp", "RN", 58, 79],
  ["riverside", "Vergie Bergstrom", "RN", 42, 76],
  ["riverside", "Kirk Braun", "RN", 33, 72],
  ["riverside", "Maximo Murphy", "CNA", 44, 72],
  ["riverside", "Ena Stamm", "LPN", 87, 93],
  ["riverside", "Abdul Cronin", "RN", 90, 93],
  ["riverside", "Korbin Rosenbaum", "CNA", 40, 70],
  ["riverside", "Aglae Hodkiewicz", "RN", 36, 69],
  ["riverside", "Ellis Wilderman", "CNA", 76, 89],
  ["riverside", "Kennedy Mohr", "RN", 96, 96],
  ["riverside", "Creola Bins", "LPN", 28, 65],
  ["riverside", "Sherry Lehner", "RN", 40, 71],
  ["riverside", "Loy Greenholt", "RN", 64, 86],
  ["riverside", "Clint Schimmel", "RN", 77, 90],
  ["riverside", "Kirk Braun", "RN", 55, 82],
  ["lakeview", "Lilyan Reichel", "RN", 27, 68],
  ["lakeview", "Kristina Jacobson", "RN", 27, 64],
  ["lakeview", "Clint Barrows", "LPN", 24, 66],
  ["lakeview", "Seamus Hoeger", "RN", 93, 95],
  ["lakeview", "Emery Zulauf", "RN", 21, 67],
  ["lakeview", "Brielle Upton", "LPN", 20, 63],
  ["lakeview", "Ignatius Paucek-Nitzsche", "RN", 47, 79],
  ["lakeview", "Ena Padberg", "RN", 32, 66],
  ["lakeview", "Modesta Abbott", "RN", 22, 65],
  ["lakeview", "Pat Yost", "CNA", 43, 75],
  ["lakeview", "Veda Moore", "RN", 72, 85],
  ["lakeview", "Dolly Lang", "CNA", 47, 78],
]

export interface DemoWorker {
  workerId: string
  facilityId: string
  name: string
  role: WorkerRole
  score: number
  /** A call-out reading imported for a roster whose outcomes are not reported (a large demo's facilities added for scale). An active worker's count is taken from their outcomes instead. */
  callOuts?: number
  chartingPct: number
  /** A recent hire (demo history plan H3): hired this many months before the demo's month, so retention shows the hire and the first months read as new. */
  hiredMonthsAgo?: number
  /** A worker who has left during the history: their last day this many days before the demo's day. */
  leftDaysAgo?: number
}

/**
 * The demo's worker who has left: the third at North Campus, so every size
 * from Minimum up has one. Last day ten days ago;
 * the roster row is marked and no shift is reported after the last day.
 */
export const DEMO_LEAVER_ID = 'demo-wkr-003'
/** The PTO balance every fixed-roster demo worker starts with, in days. */
export const DEMO_PTO_BALANCE_DAYS = 3
/**
 * How a demo worker is employed, by their position on their facility's roster
 * (the user's request, 22 October 2026, so a demo shows the bidding wizard's
 * availability step and the PRN caps): the second of each ten is part time -
 * in a Minimum demo that is Sherri, the Silver, the census-flex demo's Jordan -
 * and the seventh PRN, so both are among the first eight the demo-run step
 * bids for; the first worker of a facility, the one with the login, stays
 * full time.
 */
export function demoEmploymentType(positionInFacility: number): EmploymentType {
  switch (positionInFacility % 10) {
    case 1:
      return 'part_time'
    case 6:
      return 'prn'
    default:
      return 'full_time'
  }
}
const DEMO_LEAVER_DAYS_SINCE_LEAVING = 10

/** The leaver's last day (`YYYY-MM-DD`), reckoned from now so the demo never ages. */
export function demoLeaveDate(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - DEMO_LEAVER_DAYS_SINCE_LEAVING * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** The shortest and longest a demo worker has been employed, in days: three to ten years. */
const DEMO_TENURE_DAYS = { min: 3 * 365, max: 10 * 365 }

/**
 * A demo worker's hire date (`YYYY-MM-DD`): fixed per worker by their place
 * on the roster, reckoned from now so a demo never ages. Always at least
 * three years back, which is well clear of the longest history a demo can
 * have (24 months) plus the 90-day new-hire rule - a hire date inside that
 * would hold the worker at Probationary in their early months and undo the
 * tier story each one is scripted to tell.
 */
export function demoHireDate(workerIndex: number, now: Date): string {
  const spread = DEMO_TENURE_DAYS.max - DEMO_TENURE_DAYS.min
  // 37 steps through the range in an order that looks unsorted down a roster.
  const tenureDays = DEMO_TENURE_DAYS.min + ((workerIndex * 37 * 53) % spread)
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - tenureDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
}

/** The id the roster import gives the worker at this position in DEMO_WORKER_ROWS - fixed, so a worker keeps its id whatever subset a plan takes. */
function demoWorkerId(index: number): string {
  return `demo-wkr-${String(index + 1).padStart(3, '0')}`
}

/** The fixed roster, in import order, with the ids the import gives them. */
export const DEMO_WORKERS: readonly DemoWorker[] = DEMO_WORKER_ROWS.map(([facilityId, name, role, score, chartingPct], index) => ({
  workerId: demoWorkerId(index),
  facilityId,
  name,
  role,
  score,
  chartingPct,
}))

function tierForScore(score: number): WorkerTier {
  const { goldMinScore, silverMinScore, bronzeMinScore } = DEFAULT_FACILITY_CONFIG.tierThresholds
  if (score >= goldMinScore) {
    return 'gold'
  }
  if (score >= silverMinScore) {
    return 'silver'
  }
  return score >= bronzeMinScore ? 'bronze' : 'probationary'
}

/** Days after the scoring period resets that each tier's bid window opens - gold first, as the default config describes. */
const BID_WINDOW_OFFSET_DAYS: Record<WorkerTier, number> = { gold: 0, silver: 7, bronze: 14, probationary: 14 }

const DAY_MS = 24 * 60 * 60 * 1000

function csvField(value: string | number): string {
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** How many records go into one import call: each is written one at a time by the API, and a call must finish well inside the gateway's 30 seconds. */
const IMPORT_FILE_SIZE = 100

function chunked<T>(items: readonly T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size))
}

function toCsv(header: readonly string[], rows: readonly (readonly (string | number)[])[]): string {
  return [header, ...rows].map((row) => row.map(csvField).join(',')).join('\n') + '\n'
}

/**
 * The roster as the CSV `POST /admin/clients/{clientId}/import/workers`
 * expects - as one file or, for a large roster, several, each small enough
 * for one call. Dates are computed from `now` rather than stored, so a demo
 * set up months from today still shows upcoming bid windows, not expired ones.
 */
export function buildDemoWorkersCsvFiles(plan: DemoPlan, now: Date): string[] {
  const scoreResetsAt = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  // The reading the roster shows is the count the scoring engine will make from this month's outcomes, so the two agree from the first screen.
  const countedCallOuts = currentMonthCallOuts(plan, now)
  // The first worker of each facility is Clinical Bridge eligible, so the worker login the outcome screen hands out can enroll for a day.
  const bridgeEligibleIds = new Set(plan.workers.filter((worker, index) => plan.workers.findIndex((other) => other.facilityId === worker.facilityId) === index).map((worker) => worker.workerId))
  const unitIds = plan.units.map((unit) => unit.id)
  const rows = plan.workers.map(({ workerId, facilityId, name, role, score, callOuts, chartingPct, hiredMonthsAgo, leftDaysAgo }, workerIndex) => {
    const tier = tierForScore(score)
    const positionInFacility = plan.workers.slice(0, workerIndex).filter((other) => other.facilityId === facilityId).length
    return [
      workerId,
      facilityId,
      name,
      role,
      tier,
      score,
      countedCallOuts.get(workerId) ?? callOuts ?? '',
      chartingPct,
      new Date(scoreResetsAt).toISOString(),
      new Date(scoreResetsAt + BID_WINDOW_OFFSET_DAYS[tier] * DAY_MS).toISOString(),
      // Every demo worker has a hire date - a recent hire's within the history; the cast's come with its own rows.
      hiredMonthsAgo === undefined ? demoHireDate(workerIndex, now) : recentHireDate(hiredMonthsAgo, workerIndex, now),
      workerId === DEMO_LEAVER_ID ? demoLeaveDate(now) : leftDaysAgo === undefined ? '' : daysBefore(now, leftDaysAgo),
      bridgeEligibleIds.has(workerId) ? 'yes' : '',
      // Every fixed-roster worker has three days of PTO to ask for, so the bidding wizard has a balance to count down.
      DEMO_PTO_BALANCE_DAYS,
      // A part-timer and a PRN on every roster of ten, so the roster shows the badges and the wizard the availability step and the smaller caps.
      demoEmploymentType(positionInFacility),
      // A home unit and a float to the next (scheduling rules phase D), among the plan's units; a one-unit building floats nowhere.
      unitIds[workerIndex % unitIds.length] ?? '',
      unitIds.length > 1 ? (unitIds[(workerIndex + 1) % unitIds.length] ?? '') : '',
      `BLS:${demoCredentialLapse(now)}`,
    ]
  })
  // The cast's rows end at the hire date; nobody in the cast has left, and the cast's Bridge shifts are scripted, not enrolled.
  const castRows = plan.includesTrainingCast ? trainingWorkerRows(now, BID_WINDOW_OFFSET_DAYS, countedCallOuts).map((row) => [...row, '', '', '', '', '', '', '']) : []
  const header = ['workerId', 'facilityId', 'name', 'role', 'tier', 'score', 'callOuts', 'chartingPct', 'scoreResetsAt', 'bidWindowOpensAt', 'hireDate', 'leaveDate', 'bridgeEligible', 'ptoBalanceDays', 'employmentType', 'primaryUnit', 'secondaryUnits', 'credentials']
  return chunked([...rows, ...castRows], IMPORT_FILE_SIZE).map((fileRows) => toCsv(header, fileRows))
}

/** A date `days` before the demo's day, `YYYY-MM-DD`. */
function daysBefore(now: Date, days: number): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - days * DAY_MS).toISOString().slice(0, 10)
}

/** A recent hire's start: a weekday early in the month `monthsAgo` before the demo's, varied by roster position. */
function recentHireDate(monthsAgo: number, workerIndex: number, now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - monthsAgo, 3 + (workerIndex % 10))).toISOString().slice(0, 10)
}

/** Every demo worker's BLS lapses two years from the demo's day, well past anything a demo schedules. */
function demoCredentialLapse(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear() + 2, now.getUTCMonth(), 1)).toISOString().slice(0, 10)
}

/** The two units every demo facility schedules on, by id: the workers' home and float units name them. */
const DEMO_UNITS = ['unit-a', 'unit-b'] as const

/**
 * The two units every demo facility schedules on (census response plan F0): their beds and the census a day with no
 * count is planned on. The published shifts and the workers' home units name the same two ids.
 */
/** A demo facility's unit: its id, name, beds and the census it is planned on. */
export interface DemoUnitRecord {
  id: string
  name: string
  beds: number
  plannedCensus: number
}

export const DEMO_UNIT_RECORDS: readonly DemoUnitRecord[] = [
  // A 50-bed building in two units (demo history plan D2, 25 October 2026; 44 and 30 beds before), staffed by the
  // roster demoStaffing.ts sizes to it. A custom demo sets its own (demoPlan.ts, `customUnits`).
  { id: 'unit-a', name: 'Unit A', beds: 30, plannedCensus: 27 },
  { id: 'unit-b', name: 'Unit B', beds: 20, plannedCensus: 17 },
]

/** The default configuration for every facility in the plan, as the JSON arrays the facility-config import expects, one per call. */
export function buildDemoFacilityConfigsJsonFiles(plan: DemoPlan): string[] {
  const configs: FacilityConfig[] = plan.facilities.map(({ facilityId }) => ({
    ...DEFAULT_FACILITY_CONFIG,
    facilityId,
    // One facility with partner prioritization off, so a ranking shows both outcomes.
    prioritizationEnabled: facilityId !== plan.unprioritizedFacilityId,
    // The units with their beds, the example HPPD target rounded to the nearest seat and the lines under it (`demoStaffing.ts`),
    // so the Census tool has something to require (census response plan F0) and the facility works at about 3.4 HPPD.
    scheduling: { ...DEFAULT_SCHEDULING_RULES, units: plan.units.map((unit) => ({ ...unit })), staffing: demoStaffingPlan(), minimumStaffing: demoMinimumStaffingFor(plan.units) },
    // Quiet hours, so a text held to morning shows (SMS plan D7).
    delivery: { sms: { enabled: true, quietHours: { start: '21:00', end: '07:00' }, types: {} } },
  }))
  return chunked(configs, IMPORT_FILE_SIZE).map((file) => JSON.stringify(file))
}

/** The plan's facilities as the JSON arrays the facilities import expects, one per call. */
export function buildDemoFacilitiesJsonFiles(plan: DemoPlan): string[] {
  const facilities = plan.facilities.map(({ facilityId, name, timezone }) => ({ facilityId, name, timezone }))
  return chunked(facilities, IMPORT_FILE_SIZE).map((file) => JSON.stringify(file))
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * The email domain a demo client's generated logins live under, e.g.
 * "sussex-point-inc-4f2a.demo.example". `.example` can never be a real
 * mailbox, and the random suffix keeps two demo clients with the same name
 * from colliding on the platform-wide unique login email.
 */
export function demoLoginDomain(organizationName: string, suffix: string): string {
  return `${slugify(organizationName) || 'client'}-${suffix}.demo.example`
}

/**
 * What the onboarding form is filled with when Demo is switched on: a fake
 * organization and contact. The suffix goes into the name, so the key ID
 * derived from it and the contact's login email are both unique per demo.
 */
export function buildDemoOnboardingDetails(suffix: string): {
  organizationName: string
  contactEmail: string
  contactPhone: string
  operatorFirstName: string
  operatorLastName: string
} {
  return {
    organizationName: `Demo Health ${suffix}`,
    contactEmail: `contact@demo-health-${suffix}.demo.example`,
    contactPhone: '+1 555 010 0142',
    operatorFirstName: 'Casey',
    operatorLastName: 'Brightwell',
  }
}

// ---- Behaviors ----

/** The one behavior of its own a demo client defines. */
export const DEMO_CUSTOM_BEHAVIOR: CustomBehaviorInput = {
  key: 'in_service_done',
  label: 'In-Service Completed',
  description: 'Attended the month\u2019s mandatory in-service training',
  direction: 'positive',
  // Once a month is the requirement: full marks from one, none at none, whatever the month's length.
  basis: 'per_period_minimum',
}

/** Master changes: the custom behavior switched on at 10, taken from charting so the weights still total 100. Anchors stay at their defaults. */
export const DEMO_MASTER_SETTINGS: BehaviorSettingsInput = {
  charting_completion: { enabled: true, weight: 15 },
  [DEMO_CUSTOM_BEHAVIOR.key]: { enabled: true, weight: 10 },
}

/** One facility that measures differently: no charting, with its weight moved to call-outs and punctuality (still totalling 100), and a stricter call-out anchor. */
export const DEMO_BEHAVIOR_OVERRIDES = {
  facilityId: 'lakeview',
  overrides: {
    charting_completion: { enabled: false, weight: 15 },
    call_out_rate: { enabled: true, weight: 40, best: 0, worst: 0.15 },
    on_time_arrival: { enabled: true, weight: 25 },
  } satisfies BehaviorSettingsInput,
}

// ---- Partner activity ----

const DEMO_SHIFT_START_HOUR_UTC = 7
const DEMO_SHIFT_HOURS = 8
const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
/** Which days of the week a worker is scheduled: three a week, offset per worker so a facility's roster is spread across the week. */
const WORKING_WEEKDAYS: readonly number[] = [0, 2, 4]
const WEEK_DAYS = 7
/** A middling demo worker leaves every eighth shift's chart undone, a low scorer every fifth. */
const DEMO_MIDDLING_MISSED_CHART_EVERY = 8
const DEMO_LOW_MISSED_CHART_EVERY = 5

export type DemoOutcomeEvent =
  | { kind: 'shift-complete'; idempotencyKey: string; payload: ShiftCompleteEvent }
  | { kind: 'callout'; idempotencyKey: string; payload: CalloutEvent }
  | { kind: 'no-show'; idempotencyKey: string; payload: NoShowEvent }

/**
 * What became of one shift, decided from the score the worker is behaving
 * like that month so the record agrees with it: a high scorer works every
 * shift on time, a middling one is sometimes late or calls out, a low one
 * calls out at short notice and misses a shift outright. Fixed rules, not
 * chance, so every demo client gets the same story; `shiftNumber` counts
 * from the start of the month.
 */
function demoOutcome(worker: Pick<DemoActiveWorker, 'workerId' | 'facilityId'>, score: number, shiftNumber: number, shiftStart: number): DemoOutcomeEvent {
  const shiftEnd = shiftStart + DEMO_SHIFT_HOURS * HOUR_MS
  const iso = (time: number): string => new Date(time).toISOString()
  const base = {
    facility_id: worker.facilityId,
    worker_id: worker.workerId,
    shift_date: iso(shiftStart).slice(0, 10),
    scheduled_start: iso(shiftStart),
    scheduled_end: iso(shiftEnd),
    unit_id: DEMO_UNITS[shiftNumber % DEMO_UNITS.length] ?? 'unit-a',
  }
  // Keyed by worker and shift date, so running the same demo step twice stores nothing twice.
  const idempotencyKey = `demo-${worker.workerId}-${base.shift_date}`

  if (score < 40 && shiftNumber === 2) {
    return { kind: 'no-show', idempotencyKey, payload: base }
  }
  const callOutEvery = score >= 80 ? 0 : score >= 60 ? 7 : 4
  if (callOutEvery > 0 && shiftNumber % callOutEvery === 1) {
    const noticeHours = score < 60 ? 3 : 20
    return { kind: 'callout', idempotencyKey, payload: { ...base, reported_at: iso(shiftStart - noticeHours * HOUR_MS) } }
  }
  const isLate = score < 80 && shiftNumber % 3 === 0
  const leftEarly = score < 60 && shiftNumber % 5 === 0
  // One shift, one chart: a high scorer charts every shift, a middling one misses the odd chart, a low one more.
  const chartEvery = score >= 80 ? 0 : score >= 60 ? DEMO_MIDDLING_MISSED_CHART_EVERY : DEMO_LOW_MISSED_CHART_EVERY
  const chartCompleted = chartEvery === 0 || shiftNumber % chartEvery !== chartEvery - 1
  return {
    kind: 'shift-complete',
    idempotencyKey,
    payload: { ...base, actual_start: iso(shiftStart + (isLate ? 12 : -3) * MINUTE_MS), actual_end: iso(shiftEnd + (leftEarly ? -40 : 4) * MINUTE_MS), chart_completed: chartCompleted },
  }
}

/** The first day (UTC midnight) of the month `monthsAgo` before the one `now` is in. */
function monthStart(now: Date, monthsAgo: number): number {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - monthsAgo, 1)
}

/** The `YYYY-MM` label of the month `monthsAgo` before the current one. */
function monthLabel(now: Date, monthsAgo: number): string {
  return new Date(monthStart(now, monthsAgo)).toISOString().slice(0, 7)
}

/**
 * One worker's shifts over their span: three days a week from the first
 * day of their oldest month up to yesterday, each month's outcomes decided
 * by that month's score from the plan.
 */
function workerOutcomeEvents(worker: DemoActiveWorker, workerIndex: number, now: Date): DemoOutcomeEvent[] {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return worker.monthlyScores.flatMap((score, monthOffset) => {
    const monthsAgo = worker.months - 1 - monthOffset
    const from = monthStart(now, monthsAgo)
    const to = Math.min(monthStart(now, monthsAgo - 1), today)
    const days = Array.from({ length: Math.max(0, Math.round((to - from) / DAY_MS)) }, (_, day) => from + day * DAY_MS)
    return days
      .filter((day) => WORKING_WEEKDAYS.includes((Math.round(day / DAY_MS) + workerIndex) % WEEK_DAYS))
      .map((day, shiftNumber) => demoOutcome(worker, score, shiftNumber, day + DEMO_SHIFT_START_HOUR_UTC * HOUR_MS))
  })
}

/**
 * Every active worker's shift outcomes across their span, ending yesterday
 * - and, when the plan includes the cast, its two scripted months at
 * Harbor View.
 */
export function buildDemoOutcomeEvents(plan: DemoPlan, now: Date): DemoOutcomeEvent[] {
  const lastDay = demoLeaveDate(now)
  // Someone who has left has no shifts after their last day.
  const active = plan.activeWorkers.flatMap((worker, workerIndex) => workerOutcomeEvents(worker, workerIndex, now)).filter((event) => event.payload.worker_id !== DEMO_LEAVER_ID || event.payload.shift_date <= lastDay)
  return plan.includesTrainingCast ? [...active, ...buildTrainingOutcomeEvents(now)] : active
}

/**
 * Each worker's call-outs among this month's outcomes, counted the way the
 * engine counts them (by shift date, within the running period): what the
 * roster's call-out reading is set to, so it never disagrees with the
 * score breakdown. A worker with no outcomes this month is absent.
 */
export function currentMonthCallOuts(plan: DemoPlan, now: Date): ReadonlyMap<string, number> {
  const thisMonth = monthLabel(now, 0)
  const counts = new Map<string, number>()
  for (const event of buildDemoOutcomeEvents(plan, now)) {
    if (event.payload.shift_date.startsWith(thisMonth)) {
      const { worker_id: workerId } = event.payload
      counts.set(workerId, (counts.get(workerId) ?? 0) + (event.kind === 'callout' ? 1 : 0))
    }
  }
  return counts
}

/** An in-service attended last week, for the active workers in good standing. */
export function buildDemoBehaviorEvents(plan: DemoPlan, now: Date): { idempotencyKey: string; payload: CustomBehaviorEvent }[] {
  const occurredAt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - 5 * DAY_MS + 14 * HOUR_MS).toISOString()
  return plan.activeWorkers.filter((worker) => worker.score >= 60).map((worker) => ({
    idempotencyKey: `demo-${worker.workerId}-in-service`,
    payload: {
      facility_id: worker.facilityId,
      worker_id: worker.workerId,
      behavior_key: DEMO_CUSTOM_BEHAVIOR.key,
      occurred_at: occurredAt,
      note: 'Quarterly safety in-service',
    },
  }))
}

/**
 * Two pools per active facility for the partner to have ranked: the first few
 * active workers in roster order, and the same with someone the facility
 * has never heard of - so the history shows a reordering and how an
 * unknown worker is placed.
 */
export function buildDemoRankingRequests(plan: DemoPlan, now: Date): { facilityId: string; request: { shift_ref: string; worker_ids: string[] } }[] {
  const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + DAY_MS).toISOString().slice(0, 10)
  return plan.activeFacilities.flatMap(({ facilityId }) => {
    const pool =
      facilityId === TRAINING_FACILITY.facilityId
        ? [...TRAINING_WORKER_IDS]
        : plan.activeWorkers
            .filter((worker) => worker.facilityId === facilityId)
            .slice(0, RANKING_POOL_SIZE)
            .map((worker) => worker.workerId)
    return [
      { facilityId, request: { shift_ref: `demo-${facilityId}-${tomorrow}-day`, worker_ids: pool } },
      { facilityId, request: { shift_ref: `demo-${facilityId}-${tomorrow}-night`, worker_ids: [...pool, 'agency-temp-01'] } },
    ]
  })
}

